import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Avatar,
  Button,
  Chip,
  Rating,
  Divider,
  Paper,
  Tab,
  Tabs,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  CircularProgress,
  Badge,
  Tooltip,
  Alert,
} from '@mui/material';
import {
  Person,
  CalendarToday,
  Favorite,
  Settings,
  Notifications,
  Payment,
  Star,
  Edit,
  Delete,
  Visibility,
  Cancel,
  CheckCircle,
  Schedule,
  LocationOn,
  AttachMoney,
  Phone,
  Email,
  PhotoCamera,
  Save,
  Refresh,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import axios from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import { useNavigate } from 'react-router-dom';

const Dashboard = () => {
  const { user, updateUser } = useAuth();
  const { showSuccess, showError } = useNotification();
  const navigate = useNavigate();
  
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [profileData, setProfileData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    location: user?.location || '',
    bio: user?.bio || '',
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [notifications, setNotifications] = useState({
    email: true,
    push: true,
    sms: false,
    marketing: false,
  });
  const [editProfileDialog, setEditProfileDialog] = useState(false);
  const [changePasswordDialog, setChangePasswordDialog] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const [bookingsRes, favoritesRes] = await Promise.all([
        axios.get('/api/bookings/user'),
        axios.get('/api/users/favorites'),
      ]);
      
      setBookings(bookingsRes.data.data || mockBookings);
      setFavorites(favoritesRes.data.data || mockFavorites);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      // Use mock data for development
      setBookings(mockBookings);
      setFavorites(mockFavorites);
    } finally {
      setLoading(false);
    }
  };

  const handleProfileUpdate = async () => {
    try {
      const response = await axios.put('/api/users/profile', profileData);
      if (response.data.success) {
        updateUser(response.data.data);
        showSuccess('Perfil actualizado exitosamente');
        setEditProfileDialog(false);
      }
    } catch (error) {
      showError('Error al actualizar el perfil');
    }
  };

  const handlePasswordChange = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      showError('Las contraseñas no coinciden');
      return;
    }
    
    try {
      await axios.put('/api/users/password', {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      });
      
      showSuccess('Contraseña actualizada exitosamente');
      setChangePasswordDialog(false);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      showError('Error al cambiar la contraseña');
    }
  };

  const handleNotificationUpdate = async (key, value) => {
    try {
      const updatedNotifications = { ...notifications, [key]: value };
      await axios.put('/api/users/notifications', updatedNotifications);
      setNotifications(updatedNotifications);
      showSuccess('Configuración de notificaciones actualizada');
    } catch (error) {
      showError('Error al actualizar las notificaciones');
    }
  };

  const cancelBooking = async (bookingId) => {
    try {
      await axios.put(`/api/bookings/${bookingId}/cancel`);
      setBookings(prev => prev.map(booking => 
        booking.id === bookingId 
          ? { ...booking, status: 'cancelled' }
          : booking
      ));
      showSuccess('Reserva cancelada exitosamente');
    } catch (error) {
      showError('Error al cancelar la reserva');
    }
  };

  const removeFavorite = async (professionalId) => {
    try {
      await axios.delete(`/api/users/favorites/${professionalId}`);
      setFavorites(prev => prev.filter(fav => fav.id !== professionalId));
      showSuccess('Eliminado de favoritos');
    } catch (error) {
      showError('Error al eliminar de favoritos');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'confirmed': return 'info';
      case 'in_progress': return 'primary';
      case 'completed': return 'success';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending': return 'Pendiente';
      case 'confirmed': return 'Confirmada';
      case 'in_progress': return 'En progreso';
      case 'completed': return 'Completada';
      case 'cancelled': return 'Cancelada';
      default: return status;
    }
  };

  // Mock data for development
  const mockBookings = [
    {
      id: 1,
      service: 'Limpieza General del Hogar',
      professional: {
        name: 'María González',
        image: '/api/placeholder/50/50',
        rating: 4.9,
      },
      date: '2024-02-15',
      time: '10:00',
      price: 250,
      status: 'confirmed',
      address: 'Carrera 7 #123, Bogotá',
    },
    {
      id: 2,
      service: 'Reparación de Tuberías',
      professional: {
        name: 'Carlos Rodríguez',
        image: '/api/placeholder/50/50',
        rating: 4.8,
      },
      date: '2024-02-20',
      time: '14:00',
      price: 350,
      status: 'pending',
      address: 'Calle 26 #456, Bogotá',
    },
    {
      id: 3,
      service: 'Instalación Eléctrica',
      professional: {
        name: 'Ana Martínez',
        image: '/api/placeholder/50/50',
        rating: 5.0,
      },
      date: '2024-01-30',
      time: '09:00',
      price: 400,
      status: 'completed',
      address: 'Zona Rosa, Bogotá',
    },
  ];

  const mockFavorites = [
    {
      id: 1,
      name: 'María González',
      services: ['Limpieza del Hogar'],
      rating: 4.9,
      reviews: 127,
      image: '/api/placeholder/80/80',
      location: 'Bogotá',
      hourlyRate: 25,
    },
    {
      id: 2,
      name: 'Carlos Rodríguez',
      services: ['Plomería', 'Reparaciones'],
      rating: 4.8,
      reviews: 89,
      image: '/api/placeholder/80/80',
      location: 'Medellín',
      hourlyRate: 35,
    },
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Paper elevation={3} sx={{ p: 4, borderRadius: 3, mb: 4 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item>
              <Avatar
                src={user?.image}
                alt={user?.name}
                sx={{ width: 80, height: 80 }}
              >
                {user?.name?.charAt(0)}
              </Avatar>
            </Grid>
            <Grid item xs>
              <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
                ¡Hola, {user?.name}!
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Bienvenido a tu panel de control
              </Typography>
            </Grid>
            <Grid item>
              <Button
                variant="outlined"
                startIcon={<Edit />}
                onClick={() => setEditProfileDialog(true)}
              >
                Editar Perfil
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </motion.div>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ textAlign: 'center', p: 2 }}>
            <CalendarToday color="primary" sx={{ fontSize: 40, mb: 1 }} />
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              {bookings.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Reservas Totales
            </Typography>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ textAlign: 'center', p: 2 }}>
            <Favorite color="error" sx={{ fontSize: 40, mb: 1 }} />
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              {favorites.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Favoritos
            </Typography>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ textAlign: 'center', p: 2 }}>
            <CheckCircle color="success" sx={{ fontSize: 40, mb: 1 }} />
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              {bookings.filter(b => b.status === 'completed').length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Completadas
            </Typography>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ textAlign: 'center', p: 2 }}>
            <AttachMoney color="warning" sx={{ fontSize: 40, mb: 1 }} />
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              ${bookings.reduce((sum, b) => sum + (b.status === 'completed' ? b.price : 0), 0)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Gastado
            </Typography>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper elevation={2} sx={{ borderRadius: 3 }}>
        <Tabs
          value={tabValue}
          onChange={(e, newValue) => setTabValue(newValue)}
          variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab icon={<CalendarToday />} label="Mis Reservas" />
          <Tab icon={<Favorite />} label="Favoritos" />
          <Tab icon={<Person />} label="Perfil" />
          <Tab icon={<Settings />} label="Configuración" />
        </Tabs>

        <Box sx={{ p: 4 }}>
          {/* Bookings Tab */}
          {tabValue === 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  Mis Reservas
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={fetchDashboardData}
                >
                  Actualizar
                </Button>
              </Box>
              
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : bookings.length === 0 ? (
                <Alert severity="info">
                  No tienes reservas aún. ¡Explora nuestros servicios!
                </Alert>
              ) : (
                <List>
                  {bookings.map((booking, index) => (
                    <React.Fragment key={booking.id}>
                      <ListItem
                        sx={{
                          border: 1,
                          borderColor: 'divider',
                          borderRadius: 2,
                          mb: 2,
                          p: 2,
                        }}
                      >
                        <ListItemAvatar>
                          <Avatar src={booking.professional.image} alt={booking.professional.name} />
                        </ListItemAvatar>
                        
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                {booking.service}
                              </Typography>
                              <Chip
                                label={getStatusText(booking.status)}
                                color={getStatusColor(booking.status)}
                                size="small"
                              />
                            </Box>
                          }
                          secondary={
                            <>
                              <Typography variant="body2" color="text.secondary">
                                Profesional: {booking.professional.name}
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <CalendarToday sx={{ fontSize: 16 }} />
                                  <Typography variant="body2">
                                    {new Date(booking.date).toLocaleDateString()} - {booking.time}
                                  </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <LocationOn sx={{ fontSize: 16 }} />
                                  <Typography variant="body2">
                                    {booking.address}
                                  </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <AttachMoney sx={{ fontSize: 16 }} />
                                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                    ${booking.price}
                                  </Typography>
                                </Box>
                              </Box>
                            </>
                          }
                        />
                        
                        <ListItemSecondaryAction>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Tooltip title="Ver detalles">
                              <IconButton
                                onClick={() => navigate(`/bookings/${booking.id}`)}
                              >
                                <Visibility />
                              </IconButton>
                            </Tooltip>
                            
                            {booking.status === 'pending' && (
                              <Tooltip title="Cancelar">
                                <IconButton
                                  color="error"
                                  onClick={() => cancelBooking(booking.id)}
                                >
                                  <Cancel />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </ListItemSecondaryAction>
                      </ListItem>
                    </React.Fragment>
                  ))}
                </List>
              )}
            </motion.div>
          )}

          {/* Favorites Tab */}
          {tabValue === 1 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                Profesionales Favoritos
              </Typography>
              
              {favorites.length === 0 ? (
                <Alert severity="info">
                  No tienes profesionales favoritos aún.
                </Alert>
              ) : (
                <Grid container spacing={3}>
                  {favorites.map((professional) => (
                    <Grid item xs={12} md={6} key={professional.id}>
                      <Card sx={{ height: '100%' }}>
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            <Avatar
                              src={professional.image}
                              alt={professional.name}
                              sx={{ width: 60, height: 60 }}
                            />
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                {professional.name}
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Rating value={professional.rating} readOnly size="small" />
                                <Typography variant="body2">
                                  {professional.rating} ({professional.reviews})
                                </Typography>
                              </Box>
                            </Box>
                            <IconButton
                              color="error"
                              onClick={() => removeFavorite(professional.id)}
                            >
                              <Delete />
                            </IconButton>
                          </Box>
                          
                          <Box sx={{ mb: 2 }}>
                            {professional.services.map((service) => (
                              <Chip
                                key={service}
                                label={service}
                                size="small"
                                variant="outlined"
                                sx={{ mr: 0.5, mb: 0.5 }}
                              />
                            ))}
                          </Box>
                          
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            📍 {professional.location}
                          </Typography>
                          
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="h6" color="success.main" sx={{ fontWeight: 'bold' }}>
                              ${professional.hourlyRate?.toLocaleString('es-CO')}/servicio
                            </Typography>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => navigate(`/professionals/${professional.id}`)}
                            >
                              Ver Perfil
                            </Button>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </motion.div>
          )}

          {/* Profile Tab */}
          {tabValue === 2 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                Información del Perfil
              </Typography>
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 3 }}>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                      Datos Personales
                    </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Person color="action" />
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Nombre
                        </Typography>
                        <Typography variant="body1">
                          {user?.name || 'No especificado'}
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Email color="action" />
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Email
                        </Typography>
                        <Typography variant="body1">
                          {user?.email}
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Phone color="action" />
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Teléfono
                        </Typography>
                        <Typography variant="body1">
                          {user?.phone || 'No especificado'}
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <LocationOn color="action" />
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Ubicación
                        </Typography>
                        <Typography variant="body1">
                          {user?.location || 'No especificado'}
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 3 }}>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                      Acciones
                    </Typography>
                    
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<Edit />}
                      onClick={() => setEditProfileDialog(true)}
                      sx={{ mb: 2 }}
                    >
                      Editar Perfil
                    </Button>
                    
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<Settings />}
                      onClick={() => setChangePasswordDialog(true)}
                    >
                      Cambiar Contraseña
                    </Button>
                  </Paper>
                </Grid>
              </Grid>
            </motion.div>
          )}

          {/* Settings Tab */}
          {tabValue === 3 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                Configuración de Notificaciones
              </Typography>
              
              <Paper variant="outlined" sx={{ p: 3 }}>
                <List>
                  <ListItem>
                    <ListItemText
                      primary="Notificaciones por Email"
                      secondary="Recibe actualizaciones sobre tus reservas por email"
                    />
                    <ListItemSecondaryAction>
                      <Switch
                        checked={notifications.email}
                        onChange={(e) => handleNotificationUpdate('email', e.target.checked)}
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                  
                  <Divider />
                  
                  <ListItem>
                    <ListItemText
                      primary="Notificaciones Push"
                      secondary="Recibe notificaciones en tiempo real en tu dispositivo"
                    />
                    <ListItemSecondaryAction>
                      <Switch
                        checked={notifications.push}
                        onChange={(e) => handleNotificationUpdate('push', e.target.checked)}
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                  
                  <Divider />
                  
                  <ListItem>
                    <ListItemText
                      primary="Notificaciones SMS"
                      secondary="Recibe mensajes de texto sobre tus reservas"
                    />
                    <ListItemSecondaryAction>
                      <Switch
                        checked={notifications.sms}
                        onChange={(e) => handleNotificationUpdate('sms', e.target.checked)}
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                  
                  <Divider />
                  
                  <ListItem>
                    <ListItemText
                      primary="Marketing"
                      secondary="Recibe ofertas especiales y promociones"
                    />
                    <ListItemSecondaryAction>
                      <Switch
                        checked={notifications.marketing}
                        onChange={(e) => handleNotificationUpdate('marketing', e.target.checked)}
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                </List>
              </Paper>
            </motion.div>
          )}
        </Box>
      </Paper>

      {/* Edit Profile Dialog */}
      <Dialog open={editProfileDialog} onClose={() => setEditProfileDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Editar Perfil</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nombre"
                value={profileData.name}
                onChange={(e) => setProfileData(prev => ({ ...prev, name: e.target.value }))}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={profileData.email}
                onChange={(e) => setProfileData(prev => ({ ...prev, email: e.target.value }))}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Teléfono"
                value={profileData.phone}
                onChange={(e) => setProfileData(prev => ({ ...prev, phone: e.target.value }))}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Ubicación"
                value={profileData.location}
                onChange={(e) => setProfileData(prev => ({ ...prev, location: e.target.value }))}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Biografía"
                multiline
                rows={3}
                value={profileData.bio}
                onChange={(e) => setProfileData(prev => ({ ...prev, bio: e.target.value }))}
                placeholder="Cuéntanos un poco sobre ti..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditProfileDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleProfileUpdate}>
            Guardar Cambios
          </Button>
        </DialogActions>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={changePasswordDialog} onClose={() => setChangePasswordDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Cambiar Contraseña</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Contraseña Actual"
                type="password"
                value={passwordData.currentPassword}
                onChange={(e) => setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nueva Contraseña"
                type="password"
                value={passwordData.newPassword}
                onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Confirmar Nueva Contraseña"
                type="password"
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChangePasswordDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handlePasswordChange}>
            Cambiar Contraseña
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Dashboard;